// ---------------------------------------------------------------------------
// AppLock — Idle detection via DOM events
// ---------------------------------------------------------------------------
/**
 * Monitors user activity and fires a callback after a configurable
 * period of inactivity.
 *
 * Performance notes:
 * - Uses a single `setTimeout`, not `setInterval`.
 * - Activity handler is throttled to max once per second to avoid
 *   overhead from high-frequency events like `mousemove`.
 * - All listeners are passive (non-blocking).
 */
export class IdleDetector {
    constructor(config, onIdle, onVisibilityHidden) {
        this.config = config;
        this.onIdle = onIdle;
        this.onVisibilityHidden = onVisibilityHidden;
        this.timer = null;
        this.lastActivity = 0;
        this.throttleMs = 1000;
        this.running = false;
        this.boundOnActivity = this.onActivity.bind(this);
        this.boundOnVisibility = this.handleVisibility.bind(this);
    }
    /** Start monitoring for idle. */
    start() {
        if (this.running)
            return;
        this.running = true;
        // Attach activity listeners
        for (const event of this.config.idleEvents) {
            document.addEventListener(event, this.boundOnActivity, { passive: true });
        }
        // Visibility change (lock when tab hidden)
        if (this.config.lockOnHide) {
            document.addEventListener('visibilitychange', this.boundOnVisibility);
        }
        // Start the timer
        this.resetTimer();
    }
    /** Stop monitoring. Called when already locked. */
    stop() {
        if (!this.running)
            return;
        this.running = false;
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        for (const event of this.config.idleEvents) {
            document.removeEventListener(event, this.boundOnActivity);
        }
        document.removeEventListener('visibilitychange', this.boundOnVisibility);
    }
    /** Manually reset the idle timer (e.g., after a programmatic action). */
    reset() {
        if (this.running) {
            this.resetTimer();
        }
    }
    /** Clean up all resources. */
    destroy() {
        this.stop();
    }
    // -----------------------------------------------------------------------
    // Internal
    // -----------------------------------------------------------------------
    onActivity() {
        const now = Date.now();
        if (now - this.lastActivity < this.throttleMs)
            return;
        this.lastActivity = now;
        this.resetTimer();
    }
    resetTimer() {
        if (this.timer !== null) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(() => {
            this.onIdle();
        }, this.config.idleTimeout * 1000);
    }
    handleVisibility() {
        if (document.visibilityState === 'hidden' && this.onVisibilityHidden) {
            this.onVisibilityHidden();
        }
    }
}
//# sourceMappingURL=idle-detector.js.map